import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useAtom } from 'jotai/index';
import { useNavigate, useSearchParams } from 'react-router-dom';
import moment from 'moment/moment';
import styles from './css/index.module.scss';
import { DICTIONARY } from '../../translations/dictionary';
import useTranslations from '../../hooks/useTranslations';
import Checkbox from '../../ui-kit/Checkbox';
import AddGrayIcon from '../../assets/icons/addGray.svg';
import DeleteGrayIcon from '../../assets/icons/deleteGray.svg';
import DownloadGrayIcon from '../../assets/icons/downloadGray.svg';
import EmailGrayIcon from '../../assets/icons/emailGray.svg';
import BigAddIcon from '../../assets/icons/bigAdd.svg';
import DocumentIcon from '../../assets/icons/documentGray.svg';
import PenSmallIcon from '../../assets/icons/penSmall.svg';
import Pagination from '../../ui-kit/Pagination';
import Button from '../../ui-kit/Button';
import Loading from '../../ui-kit/Loading';
import ProjectsNavigationBlocks from './components/ProjectsNavigationBlocks';
import ProjectsSelectingButtons from './components/ProjectsSelectingButtons';
import useProjects from '../../hooks/useProjects';
import useGridLayout from '../../hooks/useGridLayout';
import MiddleEllipsisText from '../../components/MiddleEllipsisText';
import { isInitAtom, scrollableContentRefAtom } from '../../atoms/globalAtoms';
import { projectsSearchQueryAtom } from '../../atoms/projectsAtoms';
import { closeModalAtom, openConfirmModalAtom, openInfoModalAtom } from '../../atoms/modalAtoms';

// Must match .projectCard and .createProjectButton in css/index.module.scss.
const PROJECT_CARD_WIDTH = 300;
const PROJECT_CARD_HEIGHT = 173;

function MyProjects() {
  const { t } = useTranslations();
  const navigate = useNavigate();
  const [isInit] = useAtom(isInitAtom);
  const [scrollableContentRef] = useAtom(scrollableContentRefAtom);
  const [, openConfirmModal] = useAtom(openConfirmModalAtom);
  const [, closeModal] = useAtom(closeModalAtom);

  const [selectedItems, setSelectedItems] = useState([]);

  const {
    projects,
    gettingProjectsLoading,
    projectsPagesInfo,
    projectsSearchQuery: search,
    getProjects,
    onNewProjectClick,
    onDeleteProjectsClick,
    onRenameProjectClick,
    downloadProjectsArchive,
    downloadProjectsFile,
    emailProjectsArchive,
  } = useProjects({ setSelectedItems });

  const [, setProjectsSearchQuery] = useAtom(projectsSearchQueryAtom);
  const [searchParams, setSearchParams] = useSearchParams();
  const page = searchParams.get('page');
  const isMobile = window.innerWidth <= 900;
  // Search of the list on screen (null until the first list of this visit arrives).
  // The create button follows it, so it appears and hides together with the list, not on typing.
  const [loadedSearch, setLoadedSearch] = useState(null);
  const showCreateProjectButton = !loadedSearch;
  // Mobile "show more" keeps the list on screen while the next page loads.
  const isAppendingPage = isMobile && loadedSearch !== null && +page > 1 && projects.length > 0;
  const isListLoading = loadedSearch === null || (gettingProjectsLoading && !isAppendingPage);

  const gridRef = useRef(null);
  const paginationRef = useRef(null);
  const gridLayout = useGridLayout({
    gridRef,
    paginationRef,
    scrollableContentRef,
    isEnabled: !isMobile,
    itemWidth: PROJECT_CARD_WIDTH,
    itemHeight: PROJECT_CARD_HEIGHT,
    minColumns: 2,
  });

  // Desktop: as many cards as fit on the screen; the create button takes one of the cells (it is hidden while searching).
  // Mobile keeps the server default and "show more".
  const itemsOnPage = useMemo(() => {
    if (isMobile || !gridLayout) return null;
    const cells = gridLayout.columns * gridLayout.rows;
    return Math.max(1, cells - (search ? 0 : 1));
  }, [isMobile, gridLayout, search]);

  const pagesCount = useMemo(() => {
    if (!projectsPagesInfo) return 1;
    return Math.ceil(projectsPagesInfo.total_records / projectsPagesInfo.items_on_page);
  }, [projectsPagesInfo]);

  const projectsToDisplay = useMemo(() => {
    if (isMobile) return projects;
    return projects.slice(0, projectsPagesInfo?.items_on_page || 0);
  }, [projects, isMobile, projectsPagesInfo?.items_on_page]);

  const onShowMoreClick = () => {
    onPageChange(+page + 1);
  };

  const onSelectItem = (item) => {
    if (selectedItems.includes(item.id)) {
      setSelectedItems((prev) => prev.filter((i) => i !== item.id));
    } else {
      setSelectedItems((prev) => [...prev, item.id]);
    }
  };

  const onPageChange = (val) => {
    if (selectedItems.length && !isMobile) {
      openConfirmModal({
        text: t(DICTIONARY.changePageWarning),
        onButtonClick: () => {
          setSearchParams({ page: val }, { replace: true });
          setSelectedItems([]);
          closeModal();
        },
      });
    } else {
      setSearchParams({ page: val }, { replace: true });
    }
  };

  const onProjectClick = (val) => {
    navigate(`/configurations?project_id=${val.id}&page=1`);
  };

  useEffect(() => () => setProjectsSearchQuery(''), []);

  const searchRef = useRef(search);
  const pageRef = useRef(page);
  const itemsOnPageRef = useRef(itemsOnPage);

  useEffect(() => {
    if (!isInit) return;
    // On desktop the page size is known only after the grid is measured.
    if (!isMobile && !itemsOnPage) return;

    const searchChanged = searchRef.current !== search;
    const pageChanged = pageRef.current !== page;
    const previousItemsOnPage = itemsOnPageRef.current;
    searchRef.current = search;
    pageRef.current = page;
    itemsOnPageRef.current = itemsOnPage;

    if (searchChanged && page && page !== '1') {
      setSearchParams({ page: 1 }, { replace: true });
      return;
    }

    // The page size changed with the screen: open the page that holds the first project seen before.
    if (!searchChanged && page && previousItemsOnPage && itemsOnPage && previousItemsOnPage !== itemsOnPage) {
      const firstItemIndex = (+page - 1) * previousItemsOnPage;
      const nextPage = Math.floor(firstItemIndex / itemsOnPage) + 1;
      if (nextPage !== +page) {
        setSearchParams({ page: nextPage }, { replace: true });
        return;
      }
    }

    if (page) {
      getProjects({
        page, search, itemsOnPage, shouldAddToExisting: isMobile,
      }).then((isLatest) => {
        if (isLatest) setLoadedSearch(search);
      });
      if (!isMobile && (pageChanged || searchChanged)) {
        scrollableContentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else {
      onPageChange(1);
    }
  }, [page, search, isInit, itemsOnPage]);

  return (
    <div className={styles.myProject}>
      <ProjectsNavigationBlocks shouldLoadCount={false} />
      <div className={styles.buttons}>
        <ProjectsSelectingButtons
          allItems={projectsToDisplay}
          isForProjects
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
        />
      </div>

      <div
        ref={gridRef}
        className={styles.projectGrid}
        style={gridLayout ? { '--projects-grid-columns': gridLayout.columns } : undefined}
      >
        {isListLoading && (
          <div className={styles.loading}>
            <Loading height={30} width={30} />
          </div>
        )}

        {!isListLoading && showCreateProjectButton && (
          <div className={styles.createProjectButton}>
            <img src={BigAddIcon} alt="Add project" className={styles.createProjectButtonAdd} />
            <Button
              text={t(DICTIONARY.createNewProject)}
              icon={AddGrayIcon}
              width="206px"
              onClick={onNewProjectClick}
            />
          </div>
        )}

        {!isListLoading && projectsPagesInfo && !projectsToDisplay.length ? (
          <p className={classNames(styles.emptyText, { [styles.emptyTextFullRow]: !showCreateProjectButton })}>
            {loadedSearch ? t(DICTIONARY.searchEmpty) : t(DICTIONARY.projectsEmpty)}
          </p>
        ) : null}

        {!isListLoading && projectsToDisplay.map((item, index) => (
          <div key={item.id} className={styles.projectCard}>
            <div className={styles.cardHeader}>
              <Checkbox onClick={() => onSelectItem(item)} value={selectedItems.includes(item.id)} />
              {item.created_at && moment(item.created_at).isValid() ? (
                <p className={styles.cardCreatedAt}>
                  {t(DICTIONARY.created)}
                  {' '}
                  {moment(item.created_at).format('DD.MM.YY')}
                </p>
              ) : null}
            </div>
            <div className={classNames(styles.cardContent, 'pressable')} onClick={() => onProjectClick(item)}>
              <p className={styles.subtitle}>
                {item.configurations_count}
                {' '}
                {t(DICTIONARY.configurations, item.configurations_count)}
              </p>
              <MiddleEllipsisText as="h3" className={styles.title} text={item.name} />
            </div>
            <div className={styles.cardActions}>
              <div
                className={classNames('pressable')}
                onClick={() => onRenameProjectClick(item)}
              >
                <img src={PenSmallIcon} alt="" />
              </div>
              <div
                className={classNames('pressable')}
                onClick={() => downloadProjectsArchive([item.id])}
              >
                <img src={DownloadGrayIcon} alt="Download" />
              </div>
              <div
                className={classNames('pressable')}
                onClick={() => downloadProjectsFile([item.id])}
              >
                <img src={DocumentIcon} alt="Document" />
              </div>
              <div
                className={classNames('pressable')}
                onClick={() => emailProjectsArchive([item.id])}
              >
                <img src={EmailGrayIcon} alt="Email" />
              </div>
              <div
                className={classNames('pressable')}
                onClick={() => onDeleteProjectsClick([item.id])}
              >
                <img src={DeleteGrayIcon} alt="Delete" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {isMobile
        ? gettingProjectsLoading && isAppendingPage ? (
          <div className={styles.showMoreButton}>
            <Loading height={30} width={30} />
          </div>
        ) : !isListLoading && projectsPagesInfo?.last_page > projectsPagesInfo?.current_page && (
          <div className={styles.showMoreButton}>
            <Button
              onClick={onShowMoreClick}
              text={t(DICTIONARY.showMore)}
              width="290px"
              isDisabled={gettingProjectsLoading}
            />
          </div>
        ) : (
          <div ref={paginationRef}>
            <Pagination page={+page} setPage={onPageChange} pagesCount={pagesCount} />
          </div>
        )}
    </div>
  );
}

export default MyProjects;
